import React from 'react';
import PropTypes from 'prop-types';
import InnerHTML from '@Components/InnerHTML';
import UniprotPdbLinks from '@Components/Results/targets/UniprotPdbLinks';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import ReactTargetHelper from '@Components/Results/services/react-target-helper.service';
import TargetDetailsCharts from '@Components/Results/targets/TargetDetailsCharts';
import Synonyms from '@Components/Results/targets/Synonyms';

import './TargetExtendedDetails.scss';

function TargetExtendedDetails({ metadata, target, onDetailsToggle, trackingAttrs }) {
  const subunits = target.subunits || [];
  const { getProteinName } = ReactTargetHelper;

  return (
    <div className='result-detail-panel'>
      <div className='result-detail-panel-wrapper result-detail-panel-wrapper-target'>
        <i className='sub-menu-close ss-delete' onClick={onDetailsToggle}/>
        <h3 className='target-details-name'>Target details</h3>

        <ul className={subunits.length > 1 ? 'target-external-complex' : ''}>
          {subunits.map((protein, index) => (
            <li className='target-external-item' key={index}>
              <InnerHTML
                tag="div"
                className='target-external-wrapper'
                html={getProteinName(protein)}
              />
              <div className='target-external-wrapper'>
                {protein.synonyms && (
                  <Synonyms
                    list={protein.synonyms}
                    prefix={'Synonyms: '}
                    separator={', '}
                    showCount={true}
                  />
                )}
              </div>

              {protein.mutantChimeraDetails && (
                <div className='target-external-wrapper'>
                  <span className='target-external-title'>Mutant/chimera Details:</span>
                  <span className='target-external-mutant'>{protein.mutantChimeraDetails}</span>
                </div>
              )}

              {subunits.length === 1 && protein.uniprotsLinks && (
                <div className='target-external-wrapper'>
                  <UniprotPdbLinks
                    links={protein.uniprotsLinks}
                    heading={'Uniprot'}
                    headingClass={'db-links'}
                    useShowMore={false}
                    trackingAction={'uniprot'}
                    proteinType={'single'}
                    useSemicolon={false}
                    trackingAttrs={trackingAttrs}
                  />
                </div>
              )}

              {subunits.length > 1 && protein.uniprotsLinks && (
                <div className='target-external-wrapper target-external-wrapper--no-flex'>
                  <UniprotPdbLinks
                    links={protein.uniprotsLinks}
                    heading={'Uniprot'}
                    headingClass={''}
                    useShowMore={false}
                    trackingAction={'uniprot'}
                    proteinType={'complex'}
                    useSemicolon={!!protein.pdbLinks}
                    trackingAttrs={trackingAttrs}
                  />
                </div>
              )}

              {protein.uid && (
                <div className='target-external-wrapper'>
                  <span className='target-external-title'>Reaxys Target ID:</span>
                  <span className='target-external-mutant'>{protein.uid}</span>
                </div>
              )}

              {subunits.length === 1 && protein.pdbLinks && (
                <div className='target-external-wrapper'>
                  <UniprotPdbLinks
                    links={protein.pdbLinks}
                    heading={'PDB'}
                    headingClass={'db-links'}
                    useShowMore={false}
                    trackingAction={'pdb'}
                    proteinType={'single'}
                    useSemicolon={false}
                    trackingAttrs={trackingAttrs}
                  />
                </div>
              )}

              {subunits.length > 1 && protein.pdbLinks && (
                <div className='target-external-wrapper target-external-wrapper--no-flex'>
                  <UniprotPdbLinks
                    links={protein.pdbLinks}
                    heading={'PDB'}
                    headingClass={''}
                    useShowMore={false}
                    trackingAction={'pdb'}
                    proteinType={'complex'}
                    useSemicolon={false}
                    trackingAttrs={trackingAttrs}
                  />
                </div>
              )}

              {protein.goId && (
                <div className='target-external-wrapper'>
                  <span className='target-external-title'>GO ID:</span>
                  <span className='target-external-mutant'>
                    <a
                      className='target-external-ids no-animate'
                      href={protein.goLink}
                      target='_blank'
                      {...trackingAttrs}
                      data-tracking-id={protein.goId}
                      data-tracking-type='linkout'
                      data-tracking-action='go'>
                      {protein.goId}
                    </a>
                  </span>
                </div>
              )}

              {protein.interproId && (
                <div className='target-external-wrapper'>
                  <span className='target-external-title'>InterPro ID:</span>
                  <span className='target-external-mutant'>
                    <a
                      className='target-external-ids no-animate'
                      href={protein.interproLink}
                      target='_blank'
                      {...trackingAttrs}
                      data-tracking-id={protein.interproId}
                      data-tracking-type='linkout'
                      data-tracking-action='interpro'>
                      {protein.interproId}
                    </a>
                  </span>
                </div>
              )}
            </li>
          ))}
        </ul>

        <TargetDetailsCharts metadata={metadata} target={target}/>
      </div>
    </div>
  );
}

TargetExtendedDetails.propTypes = {
  metadata: MetadataPropTypesShape,
  target: PropTypes.object.isRequired,
  onDetailsToggle: PropTypes.func.isRequired,
  trackingAttrs: PropTypes.object.isRequired,
};

export default TargetExtendedDetails;
